import React, { useState, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';
import {
  Shield,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Satellite,
  Rocket,
  Layers,
  User,
  LogOut,
  Download,
  Smartphone,
  Building2,
  BarChart3,
  Settings,
  Briefcase,
  Save,
  Sparkles,
  FileText,
  Workflow,
  Globe,
  Network
} from 'lucide-react';

// Auth
import { AuthProvider, useAuth } from '@/context/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';

// AI Copilot
import BaselinxCopilot from '@/components/BaselinxCopilot';

// PWA Install Prompt
import PWAInstallPrompt from '@/components/PWAInstallPrompt';

// Import pages
import HomePage from '@/pages/HomePage';
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const CMPlanningPage = lazy(() => import('@/pages/CMPlanningPage'));
const ConfigurationIdentification = lazy(() => import('@/pages/ConfigurationIdentification'));
const ConfigurationControl = lazy(() => import('@/pages/ConfigurationControl'));
const StatusAccounting = lazy(() => import('@/pages/StatusAccounting'));
const ConfigurationAudits = lazy(() => import('@/pages/ConfigurationAudits'));
const CCBManagement = lazy(() => import('@/pages/CCBManagement'));
const SupplierFlowDown = lazy(() => import('@/pages/SupplierFlowDown'));
const DigitalCM = lazy(() => import('@/pages/DigitalCM'));
const ResourcesPage = lazy(() => import('@/pages/ResourcesPage'));
const DemoPage = lazy(() => import('@/pages/DemoPage'));
const DemoWalkthrough = lazy(() => import('@/pages/DemoWalkthrough'));
const StandardsPage = lazy(() => import('@/pages/StandardsPage'));
const ChangeManagementPage = lazy(() => import('@/pages/ChangeManagementPage'));
const PLMIntegrationPage = lazy(() => import('@/pages/PLMIntegrationPage'));
const PLMWorkflowPage = lazy(() => import('@/pages/PLMWorkflowPage'));
const DefenseAerospacePage = lazy(() => import('@/pages/DefenseAerospacePage'));
const SatelliteProgramsPage = lazy(() => import('@/pages/SatelliteProgramsPage'));
const CommercialProgramsPage = lazy(() => import('@/pages/CommercialProgramsPage'));
const AnalyticsPage = lazy(() => import('@/pages/CMAnalyticsPage'));
const LegalPage = lazy(() => import('@/pages/LegalPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const HelpPage = lazy(() => import('@/pages/HelpPage'));
const DocsPage = lazy(() => import('@/pages/DocsPage'));
const DocsPrintPage = lazy(() => import('@/pages/DocsPrintPage'));
const CyberPolicyPage = lazy(() => import('@/pages/CyberPolicyPage'));
const NISTCompliancePage = lazy(() => import('@/pages/NISTCompliancePage'));
const DataHandlingPage = lazy(() => import('@/pages/DataHandlingPage'));
const CopilotFeaturesPage = lazy(() => import('@/pages/CopilotFeaturesPage'));
const ConfigurationManagementPlanPage = lazy(() => import('@/pages/ConfigurationManagementPlanPage'));
const BrochurePage = lazy(() => import('@/pages/BrochurePage'));

// Marketing Materials
const MarketingHubPage = lazy(() => import('@/pages/marketing').then((m) => ({ default: m.MarketingHubPage })));
const OnePageOverview = lazy(() => import('@/pages/marketing').then((m) => ({ default: m.OnePageOverview })));
const CaseStudyPage = lazy(() => import('@/pages/marketing').then((m) => ({ default: m.CaseStudyPage })));
const FeatureComparisonPage = lazy(() => import('@/pages/marketing').then((m) => ({ default: m.FeatureComparisonPage })));
const SpaceBrochurePage = lazy(() => import('@/pages/marketing').then((m) => ({ default: m.SpaceBrochurePage })));
const DefenseBrochurePage = lazy(() => import('@/pages/marketing').then((m) => ({ default: m.DefenseBrochurePage })));
const CommercialBrochurePage = lazy(() => import('@/pages/marketing').then((m) => ({ default: m.CommercialBrochurePage })));
const MedicalBrochurePage = lazy(() => import('@/pages/marketing').then((m) => ({ default: m.MedicalBrochurePage })));
const AutomotiveBrochurePage = lazy(() => import('@/pages/marketing').then((m) => ({ default: m.AutomotiveBrochurePage })));

// Industry Dashboards
const CommercialManufacturingDashboard = lazy(() => import('@/pages/CommercialManufacturingDashboard'));
const StartupProductDashboard = lazy(() => import('@/pages/StartupProductDashboard'));
const AviationMobilityDashboard = lazy(() => import('@/pages/AviationMobilityDashboard'));
const EnergyIndustrialDashboard = lazy(() => import('@/pages/EnergyIndustrialDashboard'));
const SpaceSatelliteDashboard = lazy(() => import('@/pages/SpaceSatelliteDashboard'));
const ManufacturingDashboard = lazy(() => import('@/pages/ManufacturingDashboard'));

// SaaS Pages
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const SignupPage = lazy(() => import('@/pages/SignupPage'));
const SubscriptionSuccessPage = lazy(() => import('@/pages/SubscriptionSuccessPage'));
const SubscriptionCancelPage = lazy(() => import('@/pages/SubscriptionCancelPage'));
const PlatformAdminDashboard = lazy(() => import('@/pages/PlatformAdminDashboard'));

// Admin Pages
const AutomationStudioPage = lazy(() => import('@/pages/AutomationStudioPage'));
const OperationalEnvironmentsPage = lazy(() => import('@/pages/OperationalEnvironmentsPage'));
const TDSDashboardPage = lazy(() => import('@/pages/tds/TDSDashboardPage'));
const TDSPDMPage = lazy(() => import('@/pages/tds/TDSPDMPage'));
const TDSPlatformPage = lazy(() => import('@/pages/tds/TDSPlatformPage'));
const TDSProductPage = lazy(() => import('@/pages/tds/TDSProductPage'));
import GlobalSearch from '@/components/GlobalSearch';
const SoftwareDashboardPage = lazy(() => import('@/pages/software/SoftwareDashboardPage'));
const SpaceCMDashboardPage = lazy(() => import('@/pages/space-cm/SpaceCMDashboardPage'));
const DoDCMDashboardPage = lazy(() => import('@/pages/dod-cm/DoDCMDashboardPage'));
const AviationCMDashboardPage = lazy(() => import('@/pages/aviation-cm/AviationCMDashboardPage'));

// PDM / ERP / Digital Thread (new enterprise apps)
const PDMApp = lazy(() => import('@/pages/pdm/PDMApp'));
const ERPApp = lazy(() => import('@/pages/erp/ERPApp'));
const DigitalThreadPage = lazy(() => import('@/pages/DigitalThreadPage'));
const IndustryDashboard = lazy(() => import('@/pages/industry/IndustryDashboard'));
const EnterpriseSetupPage = lazy(() => import('@/pages/setup/EnterpriseSetupPage'));
import ScrollToTop from '@/components/ScrollToTop';
const PLMAgentsPage = lazy(() => import('@/pages/agents/PLMAgentsPage'));
const BusinessAIPage = lazy(() => import('@/pages/agents/BusinessAIPage'));

// Navigation Component
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // PWA Install detection
  React.useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream);

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowInstallBanner(false);
      }
      setDeferredPrompt(null);
    } else if (isIOS) {
      alert('To install Baselinx Mobile:\n\n1. Tap the Share button (box with arrow)\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" to confirm');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed w-full bg-slate-900/95 backdrop-blur-md border-b border-cyan-500/20 z-50" data-testid="main-navigation">
      <div className="px-4">
        <div className="flex justify-between items-center h-14">
          <Link to="/" className="flex items-center group flex-shrink-0">
            <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center glow-cyan">
              <Satellite className="w-5 h-5 text-white" />
            </div>
            <span className="ml-2 text-base font-heading font-bold text-white">BASELINX</span>
            <span className="ml-1 text-base font-heading font-bold text-cyan-400">PLM</span>
          </Link>
          
          {/* Desktop Menu - Compact styling */}
          <div className="hidden min-[1600px]:flex flex-1 min-w-0 justify-between items-center gap-1 mx-6 whitespace-nowrap leading-none" data-testid="desktop-menu">
            <Link to="/" className="px-1.5 py-1.5 text-cyan-300 hover:text-white hover:bg-cyan-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">Home</Link>
            
            {/* Launch App - Special Styling */}
            <Link to="/app" data-testid="topnav-launch" className="px-1.5 py-1 bg-gradient-to-r from-emerald-500/20 to-green-500/20 border border-emerald-500/40 rounded transition-all font-bold text-[10px] uppercase tracking-wide flex items-center gap-1 group hover:border-emerald-400">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse shadow-lg shadow-emerald-500/50"></span>
              <span className="text-emerald-400 group-hover:text-emerald-300">Launch</span>
            </Link>
            
            <Link to="/cm-planning" className="px-1.5 py-1.5 text-cyan-300 hover:text-white hover:bg-cyan-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">
              CM Plan
            </Link>
            
            <Link to="/configuration-identification" className="px-1.5 py-1.5 text-cyan-300 hover:text-white hover:bg-cyan-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">
              Config ID
            </Link>
            <Link to="/configuration-control" className="px-1.5 py-1.5 text-cyan-300 hover:text-white hover:bg-cyan-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">
              Control
            </Link>
            <Link to="/status-accounting" className="px-1.5 py-1.5 text-cyan-300 hover:text-white hover:bg-cyan-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">
              Status
            </Link>
            <Link to="/audits" className="px-1.5 py-1.5 text-cyan-300 hover:text-white hover:bg-cyan-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">
              Audits
            </Link>
            <Link to="/change-management" className="px-1.5 py-1.5 text-cyan-300 hover:text-white hover:bg-cyan-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">
              Changes
            </Link>
            <Link to="/plm-integration" className="px-1.5 py-1.5 text-cyan-300 hover:text-white hover:bg-cyan-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">
              PLM/ERP
            </Link>
            <Link to="/plm-workflow" className="px-1.5 py-1.5 text-cyan-300 hover:text-white hover:bg-cyan-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">
              Workflows
            </Link>
            <div className="relative group flex items-center">
              <Link to="/pdm" data-testid="topnav-pdm" className="px-1.5 py-1.5 inline-flex items-center text-cyan-300 hover:text-white hover:bg-cyan-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">
                PDM
              </Link>
              <div className="absolute left-0 top-full mt-1 w-56 bg-slate-900/95 backdrop-blur border border-cyan-500/30 rounded-lg shadow-2xl opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 overflow-hidden"
                   data-testid="topnav-plm-dropdown">
                <div className="px-3 py-1.5 text-[9px] uppercase text-cyan-500/70 tracking-wider border-b border-cyan-500/20">PDM Engineering</div>
                <Link to="/pdm" data-testid="topnav-pdm-home" className="block px-3 py-2 text-xs text-cyan-200 hover:bg-cyan-500/10">
                  <span className="font-mono text-cyan-400">PDM</span> · Engineering Dashboard
                </Link>
                <Link to="/pdm/vault" data-testid="topnav-pdm-vault" className="block px-3 py-2 text-xs text-cyan-200 hover:bg-cyan-500/10">
                  <span className="font-mono text-cyan-400">VLT</span> · File Vault
                </Link>
                <Link to="/pdm/viewer" data-testid="topnav-pdm-viewer" className="block px-3 py-2 text-xs text-cyan-200 hover:bg-cyan-500/10">
                  <span className="font-mono text-cyan-400">DWG</span> · 2D Drawing Viewer
                </Link>
                <Link to="/pdm/bom" data-testid="topnav-pdm-bom" className="block px-3 py-2 text-xs text-cyan-200 hover:bg-cyan-500/10">
                  <span className="font-mono text-cyan-400">BOM</span> · Engineering BOM
                </Link>
                <div className="px-3 py-1.5 text-[9px] uppercase text-cyan-500/70 tracking-wider border-t border-b border-cyan-500/20">PLM Environments</div>
                <Link to="/tds/product" data-testid="topnav-plm-tds" className="block px-3 py-2 text-xs text-cyan-200 hover:bg-cyan-500/10">
                  <span className="font-mono text-cyan-400">TDS</span> · Technical Data & Sustainment
                </Link>
                <Link to="/plm/space/product" data-testid="topnav-plm-space" className="block px-3 py-2 text-xs text-cyan-200 hover:bg-cyan-500/10">
                  <span className="font-mono text-cyan-400">SPC</span> · Space Systems
                </Link>
                <Link to="/plm/defense/product" data-testid="topnav-plm-defense" className="block px-3 py-2 text-xs text-cyan-200 hover:bg-cyan-500/10">
                  <span className="font-mono text-cyan-400">DEF</span> · Defense Platforms
                </Link>
                <Link to="/plm/aviation/product" data-testid="topnav-plm-aviation" className="block px-3 py-2 text-xs text-cyan-200 hover:bg-cyan-500/10">
                  <span className="font-mono text-cyan-400">AVN</span> · Aviation Systems
                </Link>
                <Link to="/tds/platform" data-testid="topnav-platform" className="block px-3 py-2 text-xs text-cyan-200 hover:bg-cyan-500/10">
                  <span className="font-mono text-cyan-400">PLT</span> · Engineering Platform
                </Link>
              </div>
            </div>

            {/* ERP dropdown — new */}
            <div className="relative group flex items-center">
              <Link to="/erp" data-testid="topnav-erp" className="px-1.5 py-1.5 inline-flex items-center text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">
                ERP
              </Link>
              <div className="absolute left-0 top-full mt-1 w-56 bg-slate-900/95 backdrop-blur border border-amber-500/30 rounded-lg shadow-2xl opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 overflow-hidden"
                   data-testid="topnav-erp-dropdown">
                <div className="px-3 py-1.5 text-[9px] uppercase text-amber-500/70 tracking-wider border-b border-amber-500/20">Enterprise Resource Planning</div>
                <Link to="/erp" className="block px-3 py-2 text-xs text-amber-200 hover:bg-amber-500/10"><span className="font-mono text-amber-400">ERP</span> · Dashboard</Link>
                <Link to="/erp/procurement" className="block px-3 py-2 text-xs text-amber-200 hover:bg-amber-500/10"><span className="font-mono text-amber-400">POS</span> · Procurement</Link>
                <Link to="/erp/inventory" className="block px-3 py-2 text-xs text-amber-200 hover:bg-amber-500/10"><span className="font-mono text-amber-400">INV</span> · Inventory</Link>
                <Link to="/erp/manufacturing" className="block px-3 py-2 text-xs text-amber-200 hover:bg-amber-500/10"><span className="font-mono text-amber-400">MFG</span> · Manufacturing</Link>
                <Link to="/erp/supply-chain" className="block px-3 py-2 text-xs text-amber-200 hover:bg-amber-500/10"><span className="font-mono text-amber-400">SCM</span> · Supply Chain</Link>
                <Link to="/erp/finance" className="block px-3 py-2 text-xs text-amber-200 hover:bg-amber-500/10"><span className="font-mono text-amber-400">FIN</span> · Finance <span className="text-[8px] text-amber-500/60">(DEMO)</span></Link>
                <Link to="/erp/quality" className="block px-3 py-2 text-xs text-amber-200 hover:bg-amber-500/10"><span className="font-mono text-amber-400">QA</span> · Quality & Traceability</Link>
              </div>
            </div>

            {/* Digital Thread */}
            <Link to="/digital-thread" data-testid="topnav-digital-thread" className="px-1.5 py-1.5 inline-flex items-center gap-1 text-fuchsia-300 hover:text-fuchsia-200 hover:bg-fuchsia-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">
              <Network className="w-3 h-3" />
              Thread
            </Link>

            {/* Standards & Analytics */}
            <Link to="/standards" className="px-1.5 py-1.5 text-cyan-300 hover:text-white hover:bg-cyan-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">Standards</Link>
            <Link to="/analytics" className="px-1.5 py-1.5 text-cyan-300 hover:text-white hover:bg-cyan-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider flex items-center gap-1">
              <BarChart3 className="w-3 h-3" />
              Analytics
            </Link>

            {/* Docs dropdown — merged Resources + Docs */}
            <div className="relative group flex items-center">
              <Link to="/docs" data-testid="topnav-docs" className="px-1.5 py-1.5 text-cyan-300 hover:text-white hover:bg-cyan-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">
                Docs
              </Link>
              <div className="absolute right-0 top-full mt-1 w-56 bg-slate-900/95 backdrop-blur border border-cyan-500/30 rounded-lg shadow-2xl opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 overflow-hidden"
                   data-testid="topnav-docs-dropdown">
                <div className="px-3 py-1.5 text-[9px] uppercase text-cyan-500/70 tracking-wider border-b border-cyan-500/20">Documentation</div>
                <Link to="/docs" data-testid="topnav-docs-hub" className="block px-3 py-2 text-xs text-cyan-200 hover:bg-cyan-500/10">
                  <span className="font-mono text-cyan-400">HUB</span> · Documentation Hub
                </Link>
                <div className="px-3 py-1.5 text-[9px] uppercase text-cyan-500/70 tracking-wider border-t border-b border-cyan-500/20">Resources</div>
                <Link to="/resources" data-testid="topnav-docs-resources" className="block px-3 py-2 text-xs text-cyan-200 hover:bg-cyan-500/10">
                  <span className="font-mono text-cyan-400">LIB</span> · Resources Library
                </Link>
              </div>
            </div>

            {isAuthenticated ? (
              <div className="contents">
                {/* Automation Studio Link */}
                <Link 
                  to="/automation" 
                  className="px-1.5 py-1.5 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider flex items-center gap-1"
                  data-testid="automation-studio-link"
                >
                  <Workflow className="w-3 h-3" />
                  Automation
                </Link>
                {/* Operational Environments Link */}
                <Link 
                  to="/environments" 
                  className="px-1.5 py-1.5 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider flex items-center gap-1"
                  data-testid="environments-link"
                >
                  <Globe className="w-3 h-3" />
                  Environments
                </Link>
                <div className="relative group flex items-center">
                  <Link to="/enterprise-setup" data-testid="topnav-admin" className="px-1.5 py-1.5 inline-flex items-center gap-1 text-sky-300 hover:text-white hover:bg-sky-500/10 rounded transition-all font-bold text-[10px] uppercase tracking-wider">
                    <Settings className="w-3 h-3" />Admin
                  </Link>
                  <div className="absolute right-0 top-full mt-1 w-60 bg-slate-900/95 backdrop-blur border border-sky-500/30 rounded-lg shadow-2xl opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 overflow-hidden" data-testid="topnav-admin-dropdown">
                    <div className="px-3 py-1.5 text-[9px] uppercase text-sky-500/70 tracking-wider border-b border-sky-500/20">Administration</div>
                    <Link to="/enterprise-setup" data-testid="topnav-admin-setup" className="block px-3 py-2 text-xs text-sky-200 hover:bg-sky-500/10"><span className="font-mono text-sky-400">ESM</span> · Enterprise Setup & Migration</Link>
                    <Link to="/enterprise-setup?step=migration" data-testid="topnav-admin-migration" className="block px-3 py-2 text-xs text-sky-200 hover:bg-sky-500/10"><span className="font-mono text-sky-400">MIG</span> · Data Migration Center</Link>
                    {user?.role === 'super_admin' && <Link to="/platform-admin" data-testid="topnav-admin-platform" className="block px-3 py-2 text-xs text-sky-200 hover:bg-sky-500/10"><span className="font-mono text-sky-400">PLT</span> · Platform Admin</Link>}
                    {user?.role === 'super_admin' && <Link to="/admin/business-ai" data-testid="topnav-admin-business-ai" className="block px-3 py-2 text-xs text-fuchsia-200 hover:bg-fuchsia-500/10"><span className="font-mono text-fuchsia-400">BIZ</span> · Business AI Command Center</Link>}
                  </div>
                </div>
              </div>
            ) : (
              <div className="contents">
                <Link to="/pricing" className="nav-cta px-3 py-1.5 text-cyan-400 hover:text-cyan-300 border border-cyan-500/30 hover:border-cyan-400/50 rounded transition-all font-bold text-[10px] uppercase">
                  Pricing
                </Link>
                <Link to="/login" className="nav-cta bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-3 py-1.5 rounded hover:shadow-lg hover:shadow-cyan-500/30 transition font-bold text-[10px] uppercase flex items-center gap-1">
                  <Rocket className="w-3 h-3" />
                  Sign In
                </Link>
              </div>
            )}
          </div>

          {isAuthenticated && (
            <div className="hidden sm:flex items-center gap-1 shrink-0 ml-auto min-[1600px]:ml-2">
              <div data-testid="topnav-user-badge" className="flex items-center gap-1.5 px-2 py-1 bg-slate-800 rounded-md border border-slate-700">
                <User className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-white text-[11px] font-medium max-w-[140px] truncate">{user?.name || user?.email}</span>
                <span className="text-[9px] px-1 py-0.5 bg-cyan-500/20 text-cyan-400 rounded font-bold">{user?.role}</span>
              </div>
              <button onClick={handleLogout} className="p-1.5 text-gray-400 hover:text-red-400 transition" title="Logout" data-testid="topnav-logout">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)} 
            className="min-[1600px]:hidden p-2 text-gray-300"
            data-testid="mobile-menu-button"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="min-[1600px]:hidden bg-slate-800 border-t border-cyan-500/20" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-3">
            {/* Install App Button - Prominent at top */}
            {!isStandalone && (
              <button 
                onClick={handleInstallClick}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-3 rounded-lg font-bold flex items-center justify-center gap-2 mb-4"
                data-testid="install-app-btn"
              >
                <Download className="w-5 h-5" />
                📱 Install Baselinx Mobile
              </button>
            )}
            <Link to="/" className="block text-gray-300 hover:text-cyan-400 font-space" onClick={() => setIsMenuOpen(false)}>Home</Link>
            <Link to="/dashboard" className="block text-cyan-400 hover:text-cyan-300 font-space font-bold" onClick={() => setIsMenuOpen(false)}>📊 Dashboard</Link>
            <Link to="/cm-planning" className="block text-gray-300 hover:text-cyan-400 font-space" onClick={() => setIsMenuOpen(false)}>CM Planning</Link>
            <Link to="/configuration-identification" className="block text-gray-300 hover:text-cyan-400 font-space" onClick={() => setIsMenuOpen(false)}>Configuration Identification</Link>
            <Link to="/configuration-control" className="block text-gray-300 hover:text-cyan-400 font-space" onClick={() => setIsMenuOpen(false)}>Configuration Control</Link>
            <Link to="/status-accounting" className="block text-gray-300 hover:text-cyan-400 font-space" onClick={() => setIsMenuOpen(false)}>Status Accounting</Link>
            <Link to="/audits" className="block text-gray-300 hover:text-cyan-400 font-space" onClick={() => setIsMenuOpen(false)}>Configuration Audits</Link>
            <Link to="/change-management" className="block text-gray-300 hover:text-cyan-400 font-space" onClick={() => setIsMenuOpen(false)}>Change Management</Link>
            <Link to="/plm-integration" className="block text-gray-300 hover:text-cyan-400 font-space" onClick={() => setIsMenuOpen(false)}>PLM & ERP</Link>
            <Link to="/plm-workflow" className="block text-gray-300 hover:text-cyan-400 font-space" onClick={() => setIsMenuOpen(false)}>PLM Workflows</Link>
            
            {/* Programs Section */}
            <div className="pt-2 border-t border-slate-700">
              <div className="text-cyan-400 text-xs font-bold uppercase mb-2">Programs</div>
              <Link to="/defense-aerospace" className="flex items-center gap-2 text-gray-300 hover:text-cyan-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>
                <Rocket className="w-4 h-4 text-cyan-400" /> Defense & Aerospace
              </Link>
              <Link to="/satellite-programs" className="flex items-center gap-2 text-gray-300 hover:text-purple-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>
                <Satellite className="w-4 h-4 text-purple-400" /> Satellite & Space
              </Link>
              <Link to="/commercial-programs" className="flex items-center gap-2 text-gray-300 hover:text-emerald-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>
                <Building2 className="w-4 h-4 text-emerald-400" /> Commercial Programs
              </Link>
            </div>
            
            <Link to="/standards" className="block text-gray-300 hover:text-cyan-400 font-space" onClick={() => setIsMenuOpen(false)}>Standards</Link>

            {/* PDM — includes Engineering Platform */}
            <div className="pt-2 border-t border-slate-700">
              <div className="text-cyan-400 text-xs font-bold uppercase mb-2">PDM / PLM</div>
              <Link to="/tds/product" className="block text-gray-300 hover:text-cyan-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>TDS · Technical Data & Sustainment</Link>
              <Link to="/plm/space/product" className="block text-gray-300 hover:text-cyan-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>SPC · Space Systems</Link>
              <Link to="/plm/defense/product" className="block text-gray-300 hover:text-cyan-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>DEF · Defense Platforms</Link>
              <Link to="/plm/aviation/product" className="block text-gray-300 hover:text-cyan-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>AVN · Aviation Systems</Link>
              <Link to="/tds/platform" className="block text-gray-300 hover:text-cyan-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>PLT · Engineering Platform</Link>
            </div>

            {/* Docs — includes Resources */}
            <div className="pt-2 border-t border-slate-700">
              <div className="text-cyan-400 text-xs font-bold uppercase mb-2">Docs</div>
              <Link to="/docs" className="block text-gray-300 hover:text-cyan-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>Documentation Hub</Link>
              <Link to="/resources" className="block text-gray-300 hover:text-cyan-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>Resources Library</Link>
            </div>

            {/* ERP — mobile */}
            <div className="pt-2 border-t border-slate-700">
              <div className="text-amber-400 text-xs font-bold uppercase mb-2">ERP</div>
              <Link to="/erp" className="block text-gray-300 hover:text-amber-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>Dashboard</Link>
              <Link to="/erp/procurement" className="block text-gray-300 hover:text-amber-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>Procurement</Link>
              <Link to="/erp/inventory" className="block text-gray-300 hover:text-amber-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>Inventory</Link>
              <Link to="/erp/manufacturing" className="block text-gray-300 hover:text-amber-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>Manufacturing</Link>
              <Link to="/erp/supply-chain" className="block text-gray-300 hover:text-amber-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>Supply Chain</Link>
              <Link to="/erp/finance" className="block text-gray-300 hover:text-amber-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>Finance (DEMO)</Link>
              <Link to="/erp/quality" className="block text-gray-300 hover:text-amber-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>Quality</Link>
            </div>

            {/* PDM — mobile */}
            <div className="pt-2 border-t border-slate-700">
              <div className="text-cyan-400 text-xs font-bold uppercase mb-2">PDM Engineering</div>
              <Link to="/pdm" className="block text-gray-300 hover:text-cyan-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>Dashboard</Link>
              <Link to="/pdm/vault" className="block text-gray-300 hover:text-cyan-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>File Vault</Link>
              <Link to="/pdm/viewer" className="block text-gray-300 hover:text-cyan-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>2D Drawing Viewer</Link>
              <Link to="/pdm/bom" className="block text-gray-300 hover:text-cyan-400 font-space py-1" onClick={() => setIsMenuOpen(false)}>Engineering BOM</Link>
            </div>

            <Link to="/digital-thread" className="block text-fuchsia-400 hover:text-fuchsia-300 font-space py-1" onClick={() => setIsMenuOpen(false)}>⇆ Enterprise Digital Thread</Link>
            {isAuthenticated ? (
              <>
                {/* Automation Studio Link */}
                <Link 
                  to="/automation" 
                  className="flex items-center gap-2 text-purple-400 hover:text-purple-300 font-space py-1 border-b border-slate-700 mb-2" 
                  onClick={() => setIsMenuOpen(false)}
                  data-testid="mobile-automation-link"
                >
                  <Workflow className="w-4 h-4" />
                  Automation Studio
                </Link>
                <div className="flex items-center gap-2 py-2 text-cyan-400">
                  <User className="w-4 h-4" />
                  <span>{user?.name || user?.email}</span>
                </div>
                <button 
                  onClick={() => { handleLogout(); setIsMenuOpen(false); }}
                  className="block w-full bg-red-500/20 text-red-400 px-4 py-2 rounded-lg text-center font-bold"
                >
                  <LogOut className="w-4 h-4 inline mr-2" />
                  Logout
                </button>
              </>
            ) : (
              <Link to="/login" className="block bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-lg text-center font-bold" onClick={() => setIsMenuOpen(false)}>Sign In</Link>
            )}
          </div>
        </div>
      )}

      {/* Mobile Install Banner - Fixed at bottom */}
      {!isStandalone && showInstallBanner && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-gradient-to-r from-slate-800 to-slate-900 border-t border-cyan-500/30 p-4 z-50 safe-area-bottom">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-sm">Get Baselinx Mobile</p>
              <p className="text-gray-400 text-xs">Install for offline access</p>
            </div>
            <button 
              onClick={handleInstallClick}
              className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg font-bold text-sm"
            >
              Install
            </button>
            <button 
              onClick={() => setShowInstallBanner(false)}
              className="text-gray-400 hover:text-white p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

// Navigation Wrapper to use hooks
const NavigationWrapper = () => {
  return <Navigation />;
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-slate-950 text-white py-10 md:py-16 px-4 sm:px-6 lg:px-8 border-t border-cyan-500/20" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8">
          {/* About Company / Founder */}
          <div className="col-span-2">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Satellite className="w-6 h-6 text-white" />
              </div>
              <span className="ml-3 text-xl font-heading font-bold">BASELINX</span>
              <span className="ml-1 text-xl font-heading font-bold text-cyan-400">PLM</span>
            </div>
            <p className="text-gray-400 font-space text-sm mb-4">
              Mission-critical configuration management for Aerospace, Defense, Satellite & Commercial Programs
            </p>
            
            {/* Founder Section */}
            <div className="border-t border-slate-800 pt-4 mt-4">
              <h4 className="font-heading font-bold mb-3 text-cyan-400 text-sm">About the Founder</h4>
              <div className="flex items-start gap-3">
                <img 
                  src="https://customer-assets-v7afamib.emergentagent.net/job_397438fc-52a3-4e4c-8aa0-250fdeb6143e/artifacts/6fc30b4ba7ab7c60_CE6F0003-4A4B-44C1-81CE-D5D66B53890D.png"
                  alt="Founder"
                  className="w-16 h-16 rounded-lg object-cover border border-slate-700"
                />
                <div>
                  <p className="text-gray-400 text-xs leading-relaxed mb-2">
                    Built from 15+ years of hands-on experience at NASA, Boeing, Raytheon, and CAE in aerospace, defense, and advanced manufacturing.
                  </p>
                  <div className="flex flex-wrap gap-1">
                    <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">CMII/PIC</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">PMP</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">Security+</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-heading font-bold mb-4 text-cyan-400">Solutions</h4>
            <ul className="space-y-2 text-gray-400 font-space text-sm">
              <li><Link to="/configuration-identification" className="hover:text-cyan-400 transition">Configuration Identification</Link></li>
              <li><Link to="/configuration-control" className="hover:text-cyan-400 transition">Configuration Control</Link></li>
              <li><Link to="/status-accounting" className="hover:text-cyan-400 transition">Status Accounting</Link></li>
              <li><Link to="/plm-workflow" className="hover:text-cyan-400 transition">PLM Workflows</Link></li>
              <li><Link to="/enterprise-setup" data-testid="footer-enterprise-setup-link" className="hover:text-cyan-400 transition">Enterprise Setup & Migration</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-bold mb-4 text-cyan-400">Programs</h4>
            <ul className="space-y-2 text-gray-400 font-space text-sm">
              <li><Link to="/defense-aerospace" className="hover:text-cyan-400 transition">Defense & Aerospace</Link></li>
              <li><Link to="/satellite-programs" className="hover:text-purple-400 transition">Satellite & Space</Link></li>
              <li><Link to="/commercial-programs" className="hover:text-emerald-400 transition">Commercial Programs</Link></li>
              <li><Link to="/standards" className="hover:text-cyan-400 transition">Standards</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-bold mb-4 text-cyan-400">Resources</h4>
            <ul className="space-y-2 text-gray-400 font-space text-sm">
              <li><Link to="/docs" className="hover:text-cyan-400 transition">User Manual</Link></li>
              <li><Link to="/resources" className="hover:text-cyan-400 transition">Documentation</Link></li>
              <li><Link to="/configuration-management-plan" className="hover:text-cyan-400 transition flex items-center gap-1"><FileText className="w-3 h-3" /> CM Plan</Link></li>
              <li><Link to="/copilot-features" className="hover:text-purple-400 transition flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI Copilot</Link></li>
              <li><Link to="/legal/privacy" className="hover:text-gray-300 transition text-gray-500">Privacy Policy</Link></li>
              <li><Link to="/legal/terms" className="hover:text-gray-300 transition text-gray-500">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 mt-12 pt-8">
          {/* Federal Registration Info */}
          <div className="mb-6">
            <p className="text-gray-500 font-space text-xs text-center mb-3">Baselinx PLM LLC — Registered Federal Contractor</p>
            <div className="flex justify-center flex-wrap gap-4 text-xs">
              <div className="bg-slate-900/50 rounded-lg px-4 py-2 border border-slate-800">
                <span className="text-gray-500">D-U-N-S® #:</span>
                <span className="text-cyan-400 font-mono ml-2">14-392-8991</span>
              </div>
              <div className="bg-slate-900/50 rounded-lg px-4 py-2 border border-slate-800">
                <span className="text-gray-500">UEI (SAM.gov):</span>
                <span className="text-cyan-400 font-mono ml-2">GEAVTX6X2685</span>
              </div>
              <div className="bg-slate-900/50 rounded-lg px-4 py-2 border border-slate-800">
                <span className="text-gray-500">CAGE Code:</span>
                <span className="text-cyan-400 font-mono ml-2">1A2G6</span>
              </div>
            </div>
            <p className="text-gray-600 text-[10px] text-center mt-3">
              Active SAM.gov Registration • Eligible for Federal Contract Awards • NAICS: 541512, 541511, 541330
            </p>
          </div>
          
          <div className="text-center">
            <p className="text-gray-500 font-space text-sm">&copy; 2026 BASELINX PLM LLC. Aligned with ANSI/EIA-649, EIA-649-1, and MIL-HDBK-61B.</p>
            <div className="flex justify-center gap-6 mt-4 text-xs text-gray-600">
              <span className="flex items-center gap-1"><Shield className="w-4 h-4 text-cyan-500" /> SOC 2 Type II</span>
              <span className="flex items-center gap-1"><Shield className="w-4 h-4 text-cyan-500" /> ITAR Compliant</span>
              <span className="flex items-center gap-1"><Shield className="w-4 h-4 text-cyan-500" /> ISO 27001</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <NavigationWrapper />
          <div className="pt-16 bg-slate-900">
            <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loading"><div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" /></div>}>
            <Routes>
              {/* Marketing/Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              
              {/* SaaS Routes - Public */}
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/signup" element={<SignupPage />} />
              
              {/* SaaS Routes - Subscription Flow */}
              <Route path="/app/subscription/success" element={<SubscriptionSuccessPage />} />
              <Route path="/app/subscription/cancel" element={<SubscriptionCancelPage />} />
              
              {/* Platform Admin Dashboard (super_admin only) */}
              <Route path="/platform-admin" element={<PlatformAdminDashboard />} />
              <Route path="/enterprise-setup" element={<EnterpriseSetupPage />} />
              <Route path="/ai-agents" element={<ProtectedRoute><PLMAgentsPage /></ProtectedRoute>} />
              <Route path="/admin/business-ai" element={<ProtectedRoute><BusinessAIPage /></ProtectedRoute>} />
              <Route path="/admin/enterprise-setup" element={<EnterpriseSetupPage />} />
              
              {/* Automation Studio */}
              <Route path="/automation" element={
                <ProtectedRoute>
                  <AutomationStudioPage />
                </ProtectedRoute>
              } />
              
              {/* Operational Environments */}
              <Route path="/environments" element={
                <ProtectedRoute>
                  <OperationalEnvironmentsPage />
                </ProtectedRoute>
              } />

              {/* Technical Data & Sustainment env */}
              <Route path="/tds" element={
                <ProtectedRoute>
                  <TDSDashboardPage />
                </ProtectedRoute>
              } />

              {/* Technical Data & Sustainment — PDM Design Vault */}
              <Route path="/tds/pdm" element={
                <ProtectedRoute>
                  <TDSPDMPage />
                </ProtectedRoute>
              } />

              {/* Technical Data & Sustainment — Engineering Platform (Systems/Quality/Suppliers/Builds) */}
              <Route path="/tds/platform" element={
                <ProtectedRoute>
                  <TDSPlatformPage />
                </ProtectedRoute>
              } />

              {/* Technical Data & Sustainment — Product Digital Thread (12 lifecycle views) */}
              <Route path="/tds/product" element={
                <ProtectedRoute>
                  <TDSProductPage />
                </ProtectedRoute>
              } />

              {/* Propagated PLM environments (Space / Defense / Aviation) — same 12-view thread */}
              <Route path="/plm/:env/product" element={
                <ProtectedRoute>
                  <TDSProductPage />
                </ProtectedRoute>
              } />

              {/* Software & Digital Systems env */}
              <Route path="/software" element={
                <ProtectedRoute>
                  <SoftwareDashboardPage />
                </ProtectedRoute>
              } />

              {/* Space & Satellite CM env */}
              <Route path="/space-cm" element={
                <ProtectedRoute>
                  <SpaceCMDashboardPage />
                </ProtectedRoute>
              } />

              {/* DoD Platforms & Baselines CM env */}
              <Route path="/dod-cm" element={
                <ProtectedRoute>
                  <DoDCMDashboardPage />
                </ProtectedRoute>
              } />

              {/* Aviation & Mobility CM env */}
              <Route path="/aviation-cm" element={
                <ProtectedRoute>
                  <AviationCMDashboardPage />
                </ProtectedRoute>
              } />
              
              <Route path="/cm-planning" element={<CMPlanningPage />} />
              <Route path="/configuration-identification" element={<ConfigurationIdentification />} />
              <Route path="/configuration-control" element={<ConfigurationControl />} />
              <Route path="/status-accounting" element={<StatusAccounting />} />
              <Route path="/audits" element={<ConfigurationAudits />} />
              <Route path="/ccb-management" element={<CCBManagement />} />
              <Route path="/supplier-flow-down" element={<SupplierFlowDown />} />
              <Route path="/digital-cm" element={<DigitalCM />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/demo-walkthrough" element={<DemoWalkthrough />} />
              <Route path="/change-management" element={<ChangeManagementPage />} />
              <Route path="/plm-integration" element={<PLMIntegrationPage />} />
              <Route path="/plm-workflow" element={<PLMWorkflowPage />} />
              <Route path="/standards" element={<StandardsPage />} />
              <Route path="/defense-aerospace" element={<DefenseAerospacePage />} />
              <Route path="/satellite-programs" element={<SatelliteProgramsPage />} />
              <Route path="/commercial-programs" element={<CommercialProgramsPage />} />
              <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
              <Route path="/cm-analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/docs/print" element={<DocsPrintPage />} />
              <Route path="/user-manual" element={<DocsPage />} />

              {/* PDM — Standalone Engineering Application */}
              <Route path="/pdm/*" element={<ProtectedRoute><PDMApp /></ProtectedRoute>} />
              {/* ERP — Enterprise Resource Planning */}
              <Route path="/erp/*" element={<ProtectedRoute><ERPApp /></ProtectedRoute>} />
              {/* Enterprise Digital Thread */}
              <Route path="/digital-thread" element={<ProtectedRoute><DigitalThreadPage /></ProtectedRoute>} />
              {/* Regulated industry environments (Healthcare / Pharma) */}
              <Route path="/industry/:envId" element={<ProtectedRoute><IndustryDashboard /></ProtectedRoute>} />
              
              {/* Compliance & Policy Routes */}
              <Route path="/cyber-policy" element={<CyberPolicyPage />} />
              <Route path="/nist-compliance" element={<NISTCompliancePage />} />
              <Route path="/data-handling" element={<DataHandlingPage />} />
              <Route path="/copilot-features" element={<CopilotFeaturesPage />} />
              <Route path="/configuration-management-plan" element={<ConfigurationManagementPlanPage />} />
              <Route path="/brochure" element={<BrochurePage />} />
              
              {/* Marketing Materials */}
              <Route path="/marketing" element={<MarketingHubPage />} />
              <Route path="/marketing/one-pager" element={<OnePageOverview />} />
              <Route path="/marketing/case-study" element={<CaseStudyPage />} />
              <Route path="/marketing/comparison" element={<FeatureComparisonPage />} />
              <Route path="/marketing/brochure/space" element={<SpaceBrochurePage />} />
              <Route path="/marketing/brochure/defense" element={<DefenseBrochurePage />} />
              <Route path="/marketing/brochure/commercial" element={<CommercialBrochurePage />} />
              <Route path="/marketing/brochure/medical" element={<MedicalBrochurePage />} />
              <Route path="/marketing/brochure/automotive" element={<AutomotiveBrochurePage />} />
              
              {/* Legal Routes */}
              <Route path="/legal/:page" element={<LegalPage />} />
              
              {/* SaaS Application Routes - Protected */}
              <Route path="/app" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />
              <Route path="/app/*" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />
              
              {/* Industry Dashboards - must come BEFORE the general /dashboard redirect */}
              <Route path="/dashboard/manufacturing" element={
                <ProtectedRoute>
                  <ManufacturingDashboard />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/commercial-manufacturing" element={
                <ProtectedRoute>
                  <CommercialManufacturingDashboard />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/startup-product" element={
                <ProtectedRoute>
                  <StartupProductDashboard />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/aviation-mobility" element={
                <ProtectedRoute>
                  <AviationMobilityDashboard />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/energy-industrial" element={
                <ProtectedRoute>
                  <EnergyIndustrialDashboard />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/space-satellite" element={
                <ProtectedRoute>
                  <SpaceSatelliteDashboard />
                </ProtectedRoute>
              } />

              {/* Legacy /dashboard route - redirect only if exact match */}
              <Route path="/dashboard" element={
                <Navigate to="/app" replace />
              } />
              
              <Route path="/bom-management" element={<Navigate to="/app" replace />} />
            </Routes>
            </Suspense>
          </div>
          <Footer />
          {/* AI Copilot - Available on all pages for authenticated users */}
          <BaselinxCopilot />
          {/* PWA Install Prompt */}
          <PWAInstallPrompt />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;